// Payment-provider resolution for the client.
//
// The backend (GET /payment/providers) decides everything: which providers are
// enabled, which is the default, whether the client may show a user-facing
// selector, and which Whop plan-id set (sandbox vs live) to send. This module
// wraps that and resolves a plan/price id for the chosen provider.
import api from '../API/baseURL';
import { getPriceId } from './stripePriceIds';
import { getWhopPlanId } from './whopPlanIds';

export const STRIPE = 'stripe';
export const WHOP = 'whop';

const SELECTED_PROVIDER_KEY = 'payment_provider';

// Sensible fallback if the providers endpoint can't be reached.
const DEFAULT_PROVIDERS_INFO = {
  enabled: [STRIPE],
  default: STRIPE,
  stripe_enabled: true,
  allow_selection: false,
  whop_sandbox: true
};

// Fetch the provider config from the backend. Never throws — returns the
// safe default on error so checkout still works (Stripe-only).
export const fetchPaymentProviders = async () => {
  try {
    const { data } = await api.get('/payment/providers');
    return { ...DEFAULT_PROVIDERS_INFO, ...(data || {}) };
  } catch (error) {
    console.error('Error fetching payment providers:', error);
    return DEFAULT_PROVIDERS_INFO;
  }
};

// Fetch the user's OWN payment provider so upgrades route to the provider they
// already subscribed with (existing Stripe subscribers keep upgrading on Stripe;
// existing Whop subscribers on Whop). Returns null for logged-out users,
// non-subscribers, or any error — callers then fall back to the default provider.
// Deliberately self-contained (no handleAuthErrors) so it NEVER redirects.
export const fetchUserProvider = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    if (!token) return null;
    const { data } = await api.get('/payment/get_unified_subscription_info', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (data && data.is_subscriber && data.provider) return data.provider;
    return null;
  } catch (error) {
    return null; // unknown -> caller uses the default provider
  }
};

// Whether to render a user-facing Stripe/Whop selector.
export const canSelectProvider = (info) =>
  !!info && info.allow_selection === true && Array.isArray(info.enabled) && info.enabled.length > 1;

// The provider to use right now: the persisted user choice when selection is
// allowed and that choice is still enabled, otherwise the backend default.
export const getActiveProvider = (info) => {
  if (!info) return STRIPE;
  if (canSelectProvider(info)) {
    const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(SELECTED_PROVIDER_KEY)) || null;
    if (stored && info.enabled.includes(stored)) return stored;
  }
  return info.default || (info.enabled && info.enabled[0]) || STRIPE;
};

// Persist the user's provider choice.
export const setActiveProvider = (provider) => {
  try {
    localStorage.setItem(SELECTED_PROVIDER_KEY, provider);
  } catch (e) {
    /* ignore storage failures */
  }
};

// Resolve the id to send to the backend for a given provider + plan + cycle.
// Stripe -> price_id; Whop -> plan_id (sandbox/live per the providers payload).
export const resolvePlanId = (provider, planName, billing, info) => {
  if (provider === WHOP) {
    const sandbox = info ? info.whop_sandbox !== false : true;
    return getWhopPlanId(planName, billing, sandbox);
  }
  return getPriceId(planName, billing);
};
